import React, { useState, useMemo, useId, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import Reveal from "../components/Reveal";
import HeroBackdrop from "../components/HeroBackdrop";
import HeroStage from "../components/HeroStage";
import Counter from "../components/Counter";
import Button from "../components/Button";
import Modal from "../components/Modal";
import DownloadDialog from "../components/DownloadDialog";
import useTilt from "../hooks/useTilt";
import { SERVICE_WORLDS } from "../data/heroWorlds";
import { getPartnerSignInUrl } from "../utils/appLinks";

/* ------------------------------------------------------------------ */
/*  Catalogue                                                           */
/* ------------------------------------------------------------------ */

const GROUPS = ["Shopping", "Leisure & Travel", "Property & Auto", "Services"];

/** Where the partner sign-in lives, for services booked through a partner. */
const PARTNER_SIGN_IN = getPartnerSignInUrl();

const SERVICES = [
  {
    id: 1,
    title: "Restaurants",
    icon: "restaurants",
    group: "Leisure & Travel",
    description:
      "Enjoy the experience of ordering your favorite meal easily and quickly from the best restaurants near you.",
    features: ["Quick Delivery", "Best Restaurants", "Easy Ordering", "Real-time Tracking"]
  },
  {
    id: 2,
    title: "Clothes",
    icon: "fashion",
    group: "Shopping",
    description:
      "Embark on a unique online shopping journey that adds more elegance and style through the app.",
    features: ["Fashion Brands", "Style Guide", "Size Matching", "Trend Updates"]
  },
  {
    id: 3,
    title: "Gifts",
    icon: "gifts",
    group: "Shopping",
    description:
      "Make your occasions more special with the finest gifts for your friends and loved ones — Mondoway is always there to share your happiness.",
    features: ["Special Occasions", "Gift Wrapping", "Personalization", "Express Delivery"]
  },
  {
    id: 4,
    title: "Electronics",
    icon: "electronics",
    group: "Shopping",
    description:
      "Stay up to date with the latest electronics and home appliances from the most renowned stores.",
    features: ["Latest Tech", "Home Appliances", "Brand Warranty", "Expert Reviews"]
  },
  {
    id: 5,
    title: "Home",
    icon: "home",
    group: "Shopping",
    description:
      "Everything you need to furnish and equip your home with greater comfort and convenience is now available.",
    features: ["Furniture", "Decor Items", "Home Essentials", "Interior Design"]
  },
  {
    id: 6,
    title: "Cars",
    icon: "cars",
    group: "Property & Auto",
    description:
      "Discover the latest cars from top global companies, all accessible within the app.",
    features: ["Global Brands", "Car Reviews", "Price Comparison", "Test Drive Booking"]
  },
  {
    id: 7,
    title: "Real Estate",
    icon: "realEstate",
    group: "Property & Auto",
    description: "Own, rent, or sell a house, apartment, villa, farm, or land with ease.",
    features: ["Buy & Sell", "Rental Services", "Property Valuation", "Legal Support"]
  },
  {
    id: 8,
    title: "Health Care",
    icon: "healthcare",
    group: "Services",
    description:
      "Your health matters; access the best clinics and doctors across all fields.",
    features: ["Expert Doctors", "Online Consultation", "Appointment Booking", "Medical Records"]
  },
  {
    id: 9,
    title: "Factories",
    icon: "factories",
    group: "Services",
    description: "Thinking of a project? Connect easily with skilled teams for support.",
    features: ["Project Management", "Skilled Teams", "Industrial Solutions", "Quality Assurance"]
  },
  {
    id: 10,
    title: "Mart Market",
    icon: "market",
    group: "Shopping",
    description: "Find all the supplies you need for your home at Mondoway Market.",
    features: ["Daily Essentials", "Fresh Products", "Bulk Orders", "Same Day Delivery"]
  },
  {
    id: 11,
    title: "Tourism",
    icon: "tourism",
    group: "Leisure & Travel",
    description:
      "Our application contains 350,000 hotels around the world. A complete trip — from hotels to organizing tours and communicating with travel agencies.",
    features: ["350K+ Hotels", "Tour Planning", "Travel Agencies", "Global Destinations"]
  },
  {
    id: 12,
    title: "Bazaar",
    icon: "bazaar",
    group: "Shopping",
    description:
      "Buy and sell used items at Mondoway Bazaar, connecting you with like-minded individuals.",
    features: ["Buy & Sell Used", "Community Marketplace", "Secure Transactions", "User Ratings"]
  },
  {
    id: 13,
    title: "Cinema",
    icon: "cinema",
    group: "Leisure & Travel",
    href: PARTNER_SIGN_IN,
    ctaLabel: "Sign in to book",
    description:
      "Browse what's showing across partner cinemas, choose your screening and seats, and hold your tickets on your phone — no queue, no printed stub.",
    features: ["Now Showing", "Seat Selection", "Instant E-Tickets", "Showtime Reminders"],
    details: {
      stepsTitle: "How booking works",
      steps: [
        {
          title: "Pick a cinema",
          desc: "Start from the cinemas nearest you and see everything screening there today."
        },
        {
          title: "Choose the film and showtime",
          desc: "Browse what's now showing and what's coming soon — each film lists its runtime, genre and age rating, with every screening for the days ahead."
        },
        {
          title: "Select your seats",
          desc: "Pick your exact seats from the hall's seating plan, so you know where you're sitting before you pay."
        },
        {
          title: "Pay and get your ticket",
          desc: "Check out in the app and your ticket is saved to your phone, ready to scan at the door — with a reminder before the film starts."
        }
      ]
    }
  }
];

const SORTS = [
  { value: "default", label: "Featured" },
  { value: "az", label: "Name (A–Z)" },
  { value: "za", label: "Name (Z–A)" },
  { value: "group", label: "Category" }
];

const TOTAL_FEATURES = SERVICES.reduce((sum, s) => sum + s.features.length, 0);

/** How many services sit in each group, for the counts on the filter chips. */
const GROUP_COUNTS = SERVICES.reduce((counts, service) => {
  counts[service.group] = (counts[service.group] || 0) + 1;
  return counts;
}, {});

/**
 * A tint per group, so thirteen cards in a row stop reading as one wall of the
 * same purple. They are four steps along the brand's own violet ramp rather
 * than four different hues — the page should look sorted, not carnival.
 */
const GROUP_ACCENTS = {
  Shopping: {
    tint: "#6A0DAD",
    deep: "#4C087D",
    soft: "#F4E8FF",
    ring: "#EADCFB",
    glow: "rgba(106,13,173,0.55)"
  },
  "Leisure & Travel": {
    tint: "#6D28D9",
    deep: "#3B1178",
    soft: "#EDE9FE",
    ring: "#DDD6FE",
    glow: "rgba(109,40,217,0.55)"
  },
  "Property & Auto": {
    tint: "#7C3AED",
    deep: "#4C1D95",
    soft: "#F1EAFE",
    ring: "#E3D7FB",
    glow: "rgba(124,58,237,0.55)"
  },
  Services: {
    tint: "#9333EA",
    deep: "#5B21B6",
    soft: "#F5E9FF",
    ring: "#E9D5FF",
    glow: "rgba(147,51,234,0.55)"
  }
};

const accentFor = (group) => GROUP_ACCENTS[group] || GROUP_ACCENTS.Shopping;

/* ------------------------------------------------------------------ */
/*  Service card                                                        */
/* ------------------------------------------------------------------ */

/** Fine diagonal hatching across the cover band, so the tint has a weave. */
const BAND_WEAVE =
  "repeating-linear-gradient(135deg, rgba(255,255,255,0.85) 0 1px, transparent 1px 11px)";

/** The plate the service glyph sits on. Shared by both views, sized by class. */
function ServiceGlyph({ icon, plate, glyph, ring }) {
  return (
    <span
      className={`flex items-center justify-center rounded-2xl bg-[linear-gradient(140deg,var(--mw-card-tint),var(--mw-card-deep))] text-white shadow-[0_16px_32px_-12px_var(--mw-card-glow)] transition-all duration-500 [transition-timing-function:var(--mw-spring)] group-hover:-translate-y-1 group-hover:shadow-[0_26px_44px_-14px_var(--mw-card-glow)] ${plate} ${ring}`}
    >
      <Icon name={icon} className={glyph} />
    </span>
  );
}

/**
 * The circular affordance in the corner, and the card's real control.
 *
 * The card as a whole is clickable, but that is a mouse convenience hung off
 * the article — this is the button a keyboard or a screen reader actually
 * reaches, so it carries the label and the focus ring.
 */
function ServiceArrow({ title, onOpen }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--mw-card-ring)] text-[var(--mw-card-tint)] transition-all duration-500 [transition-timing-function:var(--mw-spring)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/25 group-hover:-rotate-45 group-hover:border-[var(--mw-card-tint)] group-hover:bg-[var(--mw-card-tint)] group-hover:text-white group-hover:shadow-[0_12px_24px_-10px_var(--mw-card-glow)]"
    >
      <Icon name="arrow" className="h-[18px] w-[18px]" />
      <span className="sr-only">View details for {title}</span>
    </button>
  );
}

/** Everything the service ships, as a checklist rather than a chip soup. */
function ServiceFeatures({ features, className = "grid-cols-2" }) {
  return (
    <ul className={`grid gap-x-3 gap-y-2 ${className}`}>
      {features.map((feature) => (
        <li
          key={feature}
          className="flex items-start gap-2 text-[12.5px] font-medium leading-snug text-[#4D4B46]"
        >
          <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--mw-card-soft)] text-[var(--mw-card-tint)]">
            <Icon name="check" className="h-2.5 w-2.5" />
          </span>
          {feature}
        </li>
      ))}
    </ul>
  );
}

function ServiceCard({ service, index, view, onOpen }) {
  const tilt = useTilt({ max: 5, lift: 6 });
  const list = view === "list";
  const accent = accentFor(service.group);

  // The group's colours ride down as custom properties instead of a dozen
  // inline styles, which is what lets the parts underneath use them in hover
  // variants — an inline style can't be conditioned on `group-hover`.
  const palette = {
    "--mw-card-tint": accent.tint,
    "--mw-card-deep": accent.deep,
    "--mw-card-soft": accent.soft,
    "--mw-card-ring": accent.ring,
    "--mw-card-glow": accent.glow
  };

  return (
    <Reveal delay={(index % 3) * 70} className="h-full">
      <div className="mw-scene group relative h-full" style={palette}>
        {/* Gradient border in the group's own tint, faded in on hover. */}
        <div
          className="absolute -inset-px rounded-[1.5rem] bg-[linear-gradient(135deg,var(--mw-card-ring),var(--mw-card-tint)_55%,var(--mw-card-deep))] opacity-0 blur-[0.5px] transition-opacity duration-500 group-hover:opacity-100"
          aria-hidden="true"
        />

        {/* Clicking anywhere on the card opens it. This is a plain bubbling
            handler rather than a stretched overlay button: inside a
            preserve-3d card the layers are hit-tested by depth, not z-index,
            so an overlay at translateZ(0) would sit behind every mw-z layer
            and swallow nothing. The real control is the arrow in the corner. */}
        <article
          ref={tilt.ref}
          onPointerMove={tilt.onPointerMove}
          onPointerLeave={tilt.onPointerLeave}
          onClick={() => onOpen(service)}
          className={`mw-3d relative flex h-full cursor-pointer flex-col rounded-[1.5rem] border border-[#EAE2D6] bg-white shadow-[0_1px_2px_rgba(30,27,22,0.04)] group-hover:shadow-[0_38px_70px_-32px_var(--mw-card-glow)] ${
            list ? "sm:flex-row sm:items-center sm:gap-6 sm:p-6" : ""
          }`}
        >
          {/* No z-index: the content below is positioned and comes later in
              the DOM, so it paints over this rather than under it. */}
          <span
            className="mw-specular pointer-events-none absolute inset-0 rounded-[inherit]"
            aria-hidden="true"
          />

          {list ? (
            /* ------------------------------ list ------------------------- */
            <>
              {/* Clipped in its own wrapper: overflow forces a flat transform
                  style, so putting it on .mw-3d would drop every mw-z layer
                  back onto the card face. */}
              <span
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
                aria-hidden="true"
              >
                <span className="absolute -right-8 -top-10 block rotate-12 text-[var(--mw-card-tint)] opacity-[0.06] transition-transform duration-700 [transition-timing-function:var(--mw-glide)] group-hover:rotate-0 group-hover:scale-110">
                  <Icon name={service.icon} className="h-36 w-36" />
                </span>
              </span>

              <div className="mw-z3 relative p-6 pb-0 sm:p-0">
                <ServiceGlyph
                  icon={service.icon}
                  plate="h-14 w-14"
                  glyph="h-7 w-7"
                  ring="ring-1 ring-white/40"
                />
              </div>

              <div className="mw-z1 relative min-w-0 flex-1 p-6 pt-4 sm:p-0">
                <span className="mw-eyebrow text-[var(--mw-card-tint)]">
                  {service.group}
                </span>
                <h3 className="mt-1.5 text-xl font-semibold tracking-tight text-[#1E1B16]">
                  {service.title}
                </h3>
                <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-[#4D4B46]">
                  {service.description}
                </p>
                <ServiceFeatures
                  features={service.features}
                  className="mt-4 grid-cols-2 lg:grid-cols-4"
                />
              </div>

              <div className="mw-z1 relative flex shrink-0 justify-end px-6 pb-6 sm:block sm:p-0">
                <ServiceArrow title={service.title} onOpen={() => onOpen(service)} />
              </div>
            </>
          ) : (
            /* ------------------------------ grid ------------------------- */
            <>
              {/* The cover band. It carries the tint, the weave and the ghost
                  of the service's own glyph, and it is what gives the card a
                  head instead of a stack of centred elements. */}
              <div
                /* 23px, not 1.5rem: the card's 24px radius minus its 1px
                   border is the curve the band actually sits inside. */
                className="relative h-[6.5rem] shrink-0 overflow-hidden rounded-t-[23px] bg-[linear-gradient(135deg,var(--mw-card-soft),#FFFFFF_78%)]"
              >
                {/* The band is decorative but the badge on it is not, so the
                    texture is hidden a layer at a time rather than the whole
                    band being taken away from a screen reader. */}
                <span
                  className="absolute inset-0 opacity-60"
                  style={{ backgroundImage: BAND_WEAVE }}
                  aria-hidden="true"
                />
                <span
                  className="absolute -right-6 -top-4 block rotate-12 text-[var(--mw-card-tint)] opacity-[0.11] transition-transform duration-700 [transition-timing-function:var(--mw-glide)] group-hover:rotate-0 group-hover:scale-110"
                  aria-hidden="true"
                >
                  <Icon name={service.icon} className="h-32 w-32" />
                </span>
                <span className="absolute right-4 top-4 rounded-full bg-white/80 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[var(--mw-card-tint)] ring-1 ring-[var(--mw-card-ring)] backdrop-blur">
                  {service.group}
                </span>
                <span
                  className="absolute inset-x-0 bottom-0 h-px bg-[#EFE7DC]"
                  aria-hidden="true"
                />
              </div>

              {/* Straddles the band's edge, punched out of the card with a
                  white ring — which is what reads as depth rather than an
                  icon dropped on a coloured rectangle. */}
              <div className="mw-z3 relative -mt-8 px-6">
                <ServiceGlyph
                  icon={service.icon}
                  plate="h-16 w-16"
                  glyph="h-8 w-8"
                  ring="ring-4 ring-white"
                />
              </div>

              <div className="mw-z1 relative flex flex-1 flex-col px-6 pb-6 pt-5">
                <h3 className="text-xl font-semibold tracking-tight text-[#1E1B16]">
                  {service.title}
                </h3>
                <p className="mt-2.5 text-[14.5px] leading-relaxed text-[#4D4B46]">
                  {service.description}
                </p>

                <ServiceFeatures features={service.features} className="mt-5" />

                {/* mt-auto pins the footer to the bottom, so a short
                    description and a long one still line their rules up
                    across a row of cards. */}
                <div className="mt-auto flex items-center justify-between gap-4 border-t border-[#F0EAE1] pt-5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#9A9085]">
                    {service.features.length} features
                  </span>
                  <ServiceArrow title={service.title} onOpen={() => onOpen(service)} />
                </div>
              </div>
            </>
          )}
        </article>
      </div>
    </Reveal>
  );
}

/* ------------------------------------------------------------------ */
/*  Empty state                                                         */
/* ------------------------------------------------------------------ */

function EmptyState({ onReset }) {
  return (
    <div className="relative overflow-hidden rounded-[1.5rem] border border-[#EAE2D6] bg-white px-6 py-20 text-center">
      <div className="pointer-events-none absolute inset-0 [perspective:900px]" aria-hidden="true">
        <div
          className="mw-shape absolute left-[18%] top-[22%] h-20 w-20 rounded-[1.4rem] border border-[#EADCFB] bg-gradient-to-br from-[#F4E8FF] to-white shadow-[0_20px_40px_-20px_rgba(106,13,173,0.4)]"
          style={{ "--mw-sx": "24deg", "--mw-sy": "-20deg" }}
        />
        <div
          className="mw-shape absolute right-[20%] top-[30%] h-14 w-14 rounded-full border border-[#EADCFB] bg-gradient-to-br from-[#EADCFB] to-white shadow-[0_20px_40px_-20px_rgba(106,13,173,0.4)]"
          style={{ "--mw-sx": "-16deg", "--mw-sy": "22deg", animationDelay: "-5s" }}
        />
        <div
          className="mw-shape absolute bottom-[18%] left-[30%] h-12 w-12 rounded-[1rem] border border-[#EADCFB] bg-gradient-to-br from-white to-[#F4E8FF] shadow-[0_20px_40px_-20px_rgba(106,13,173,0.4)]"
          style={{ "--mw-sx": "30deg", "--mw-sy": "14deg", animationDelay: "-8s" }}
        />
      </div>

      <div className="relative">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6A0DAD] to-[#4C087D] text-white shadow-xl shadow-[#6A0DAD]/30">
          <Icon name="search" className="h-8 w-8" />
        </span>
        <h3 className="mt-7 text-2xl font-semibold tracking-tight text-[#1E1B16]">
          No services match that search
        </h3>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[#4D4B46]">
          Try a different word, or clear the filters to browse every Mondoway service.
        </p>
        <Button onClick={onReset} icon="reset" size="md" className="mt-8">
          Reset filters
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

function ServicesPage() {
  const navigate = useNavigate();
  const searchId = useId();
  const sortId = useId();

  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("All");
  const [sort, setSort] = useState("default");
  const [view, setView] = useState("grid");
  const [active, setActive] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    let result = SERVICES.filter((service) => {
      const inGroup = group === "All" || service.group === group;
      if (!inGroup) return false;
      if (!needle) return true;

      return (
        service.title.toLowerCase().includes(needle) ||
        service.description.toLowerCase().includes(needle) ||
        service.features.some((f) => f.toLowerCase().includes(needle))
      );
    });

    if (sort === "az") result = [...result].sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "za") result = [...result].sort((a, b) => b.title.localeCompare(a.title));
    if (sort === "group")
      result = [...result].sort(
        (a, b) => a.group.localeCompare(b.group) || a.title.localeCompare(b.title)
      );

    return result;
  }, [query, group, sort]);

  const filtersActive = query !== "" || group !== "All" || sort !== "default";

  // Picking a world in the hero is the same act as picking its chip in the
  // toolbar. Stable identity so the stage's own callbacks aren't rebuilt on
  // every keystroke in the search box.
  const handleWorldChange = useCallback((nextWorld) => {
    setGroup(nextWorld.key);
  }, []);

  // The detail sheet is painted in the group of whichever card opened it, so
  // a violet card doesn't open a purple dialog.
  const modalAccent = accentFor(active?.group);
  const modalPalette = {
    "--mw-card-tint": modalAccent.tint,
    "--mw-card-deep": modalAccent.deep,
    "--mw-card-soft": modalAccent.soft,
    "--mw-card-ring": modalAccent.ring,
    "--mw-card-glow": modalAccent.glow
  };

  const resetFilters = () => {
    setQuery("");
    setGroup("All");
    setSort("default");
  };

  return (
    <div className="bg-[#FAF8F2] text-[#1E1B16]">
      {/* ============================== HERO ==============================
          The same stage the landing page opens on, switched between the four
          catalogue groups instead of three moods — and wired to the filter, so
          picking a group here is the same act as picking its chip in the
          toolbar below. */}
      <HeroStage
        worlds={SERVICE_WORLDS}
        highlightKey={group === "All" ? null : group}
        onWorldChange={handleWorldChange}
        eyebrow={
          <span className="inline-flex w-fit items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[13px] font-semibold tracking-wide text-white backdrop-blur">
            <Icon name="sparkle" className="h-4 w-4 text-[#FBCFE8]" />
            {SERVICES.length} services, one platform
          </span>
        }
        title={
          <>
            Our
            <br />
            <span className="bg-[linear-gradient(100deg,#FBCFE8_0%,#E9D5FF_45%,#FFFFFF_100%)] bg-clip-text text-transparent">
              services.
            </span>
          </>
        }
        lede={
          <>
            More than an app — a fully integrated platform built to save you
            time and effort. Thirteen services, four groups, and one account
            that carries all of them.
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => setDownloadOpen(true)}
              className="group flex items-center gap-5 rounded-full bg-black/45 py-1.5 pl-6 pr-1.5 text-[15px] font-bold text-white ring-1 ring-white/15 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-black/65 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/50"
            >
              Download the app
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FBCFE8] text-[#3B0764] transition-transform duration-500 [transition-timing-function:var(--mw-spring)] group-hover:rotate-90">
                <Icon name="arrow" className="h-[18px] w-[18px]" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/about")}
              className="rounded-full border border-white/20 px-6 py-3 text-[15px] font-semibold text-white/90 transition-colors duration-300 hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white/25"
            >
              Learn more
            </button>
          </>
        }
        footnote={
          <a
            href="#catalogue"
            className="group inline-flex items-center gap-4 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FBCFE8]/40"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/[0.07] text-[#FBCFE8] backdrop-blur transition-transform duration-500 [transition-timing-function:var(--mw-spring)] group-hover:translate-y-1">
              <Icon name="chevron" className="h-[22px] w-[22px]" />
            </span>
            <span className="flex flex-col text-left">
              <span className="mw-eyebrow text-[#B9A0D6]">Browse everything</span>
              <span className="text-[0.85rem] font-semibold text-white">
                Search, filter and compare all {SERVICES.length}
              </span>
            </span>
          </a>
        }
        sideTitle={
          <>
            Pick a
            <br />
            <span className="bg-[linear-gradient(100deg,#FFFFFF_0%,#E9D5FF_55%,#FBCFE8_100%)] bg-clip-text text-transparent">
              world.
            </span>
          </>
        }
      >
        <div className="mx-auto max-w-7xl px-6 pb-20 md:px-10">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-white/12 bg-white/[0.06] backdrop-blur-xl">
              <div className="grid grid-cols-2 divide-x divide-y divide-white/10 sm:grid-cols-4 sm:divide-y-0">
                {[
                  { value: SERVICES.length, label: "Services" },
                  { value: 350, suffix: "K+", label: "Hotels worldwide" },
                  { value: TOTAL_FEATURES, suffix: "+", label: "Built-in features" },
                  { display: "24/7", label: "Support" }
                ].map((stat) => (
                  <div key={stat.label} className="px-4 py-7 text-center">
                    <div className="text-3xl font-bold tracking-tight text-white">
                      <Counter
                        value={stat.value}
                        display={stat.display}
                        suffix={stat.suffix}
                      />
                    </div>
                    <div className="mt-2 text-sm font-medium text-[#B9A0D6]">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </HeroStage>

      {/* ============================ TOOLBAR ============================= */}
      <div className="sticky top-[68px] z-30 border-y border-[#EAE2D6] bg-[#FAF8F2]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 py-4 md:px-10">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Search */}
            <div className="relative lg:w-80">
              <label htmlFor={searchId} className="sr-only">
                Search services
              </label>
              <Icon
                name="search"
                className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#7A6F61]"
              />
              <input
                id={searchId}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search services or features…"
                className="w-full rounded-xl border border-[#EAE2D6] bg-white py-2.5 pl-11 pr-4 text-[15px] text-[#1E1B16] placeholder:text-[#9A9085] transition-all duration-300 focus:border-[#D8B4FE] focus:outline-none focus:ring-4 focus:ring-[#6A0DAD]/15"
              />
            </div>

            {/* Category chips */}
            <div
              className="-mx-1 flex flex-1 items-center gap-2 overflow-x-auto px-1 pb-1 lg:justify-center lg:overflow-visible lg:pb-0"
              role="group"
              aria-label="Filter by category"
            >
              {["All", ...GROUPS].map((name) => {
                const selected = group === name;
                const accent = accentFor(name);
                const count = name === "All" ? SERVICES.length : GROUP_COUNTS[name] || 0;

                return (
                  <button
                    key={name}
                    onClick={() => setGroup(name)}
                    aria-pressed={selected}
                    className={`group/chip flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 [transition-timing-function:var(--mw-spring)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 ${
                      selected
                        ? "text-white shadow-[0_14px_26px_-12px_var(--mw-chip-glow)]"
                        : "border border-[#EAE2D6] bg-white text-[#4D4B46] hover:-translate-y-0.5 hover:border-[#D8B4FE] hover:text-[#6A0DAD]"
                    }`}
                    style={
                      selected
                        ? {
                            background: `linear-gradient(135deg, ${accent.tint}, ${accent.deep})`,
                            "--mw-chip-glow": accent.glow
                          }
                        : undefined
                    }
                  >
                    {name}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums transition-colors duration-300 ${
                        selected
                          ? "bg-white/20 text-white"
                          : "bg-[#FAF8F2] text-[#7A6F61] group-hover/chip:bg-[#F4E8FF] group-hover/chip:text-[#6A0DAD]"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sort, view, reset */}
            <div className="flex items-center gap-2">
              <label htmlFor={sortId} className="sr-only">
                Sort services
              </label>
              <div className="relative">
                <Icon
                  name="sliders"
                  className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#7A6F61]"
                />
                <select
                  id={sortId}
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="appearance-none rounded-xl border border-[#EAE2D6] bg-white py-2.5 pl-10 pr-9 text-sm font-medium text-[#4D4B46] transition-all duration-300 focus:border-[#D8B4FE] focus:outline-none focus:ring-4 focus:ring-[#6A0DAD]/15"
                >
                  {SORTS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className="flex items-center gap-1 rounded-xl border border-[#EAE2D6] bg-white p-1"
                role="group"
                aria-label="View mode"
              >
                {[
                  { mode: "grid", icon: "grid", label: "Grid view" },
                  { mode: "list", icon: "list", label: "List view" }
                ].map((option) => (
                  <button
                    key={option.mode}
                    onClick={() => setView(option.mode)}
                    aria-pressed={view === option.mode}
                    aria-label={option.label}
                    title={option.label}
                    className={`rounded-lg p-2 transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 ${
                      view === option.mode
                        ? "bg-[#F4E8FF] text-[#6A0DAD]"
                        : "text-[#7A6F61] hover:text-[#6A0DAD]"
                    }`}
                  >
                    <Icon name={option.icon} className="h-[18px] w-[18px]" />
                  </button>
                ))}
              </div>

              <button
                onClick={resetFilters}
                disabled={!filtersActive}
                title="Reset filters"
                className="rounded-xl border border-[#EAE2D6] bg-white p-2.5 text-[#7A6F61] transition-all duration-300 hover:border-[#D8B4FE] hover:text-[#6A0DAD] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#EAE2D6] disabled:hover:text-[#7A6F61]"
              >
                <Icon name="reset" className="h-[18px] w-[18px]" />
                <span className="sr-only">Reset filters</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============================ RESULTS ============================= */}
      <section id="catalogue" className="scroll-mt-32 py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="mw-eyebrow text-[#6A0DAD]">
                {group === "All" ? "The catalogue" : group}
              </span>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-[#1E1B16] sm:text-3xl">
                {query.trim()
                  ? `Results for “${query.trim()}”`
                  : group === "All"
                    ? "Every Mondoway service"
                    : `${group} services`}
              </h2>
            </div>

            <p className="text-sm font-medium text-[#7A6F61]" aria-live="polite">
              Showing{" "}
              <span className="font-bold tabular-nums text-[#1E1B16]">
                {filtered.length}
              </span>{" "}
              of {SERVICES.length} services
              {group !== "All" && ` in ${group}`}
            </p>
          </div>

          {filtered.length === 0 ? (
            <EmptyState onReset={resetFilters} />
          ) : (
            <div
              className={
                view === "grid"
                  ? "grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                  : "flex flex-col gap-4"
              }
            >
              {filtered.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  index={index}
                  view={view}
                  onOpen={setActive}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================== CTA =============================== */}
      <section className="relative isolate overflow-hidden bg-[#220339]">
        <HeroBackdrop tone="cta" />

        <div className="relative mx-auto max-w-4xl px-6 py-28 text-center md:px-10 lg:py-32">
          <Reveal>
            <h2 className="mw-h2 text-white">Ready to experience Mondoway?</h2>
            <p className="mw-lede mx-auto mt-6 max-w-2xl text-[#DCC9F2]">
              Download the app today and discover all services in one convenient
              platform.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-11 flex flex-col justify-center gap-3.5 sm:flex-row">
              <Button
                variant="light"
                icon="arrow"
                onClick={() => setDownloadOpen(true)}
              >
                Download the app
              </Button>
              <Button variant="glass" onClick={() => navigate("/contact")}>
                Talk to us
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <DownloadDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} />

      {/* ============================= MODAL ============================== */}
      <Modal
        open={active !== null}
        onClose={() => setActive(null)}
        labelledBy="service-modal-title"
      >
        {active && (
          <div className="p-8 sm:p-10" style={modalPalette}>
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[linear-gradient(140deg,var(--mw-card-tint),var(--mw-card-deep))] text-white shadow-[0_20px_40px_-14px_var(--mw-card-glow)]">
              <Icon name={active.icon} className="h-8 w-8" />
            </span>

            <span className="mt-6 inline-block rounded-full border border-[var(--mw-card-ring)] bg-[var(--mw-card-soft)] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--mw-card-tint)]">
              {active.group}
            </span>

            <h2
              id="service-modal-title"
              className="mt-4 text-3xl font-bold tracking-tight text-[#1E1B16]"
            >
              {active.title}
            </h2>

            <p className="mt-4 text-[17px] leading-relaxed text-[#4D4B46]">
              {active.description}
            </p>

            {active.details?.steps && (
              <>
                <h3 className="mw-eyebrow mt-9 text-[var(--mw-card-tint)]">
                  {active.details.stepsTitle}
                </h3>
                <ol className="mt-5 space-y-4">
                  {active.details.steps.map((step, stepIndex) => (
                    <li key={step.title} className="flex gap-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(140deg,var(--mw-card-tint),var(--mw-card-deep))] text-[13px] font-bold text-white">
                        {stepIndex + 1}
                      </span>
                      <span className="pt-0.5">
                        <span className="block text-[16px] font-semibold text-[#1E1B16]">
                          {step.title}
                        </span>
                        <span className="mt-1 block text-[15px] leading-relaxed text-[#4D4B46]">
                          {step.desc}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            )}

            <h3 className="mw-eyebrow mt-9 text-[var(--mw-card-tint)]">What&rsquo;s included</h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {active.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-center gap-3 rounded-xl border border-[#EAE2D6] bg-white/70 px-4 py-3 text-[15px] font-medium text-[#1E1B16]"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--mw-card-soft)] text-[var(--mw-card-tint)]">
                    <Icon name="check" className="h-4 w-4" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {active.href ? (
                <Button
                  size="md"
                  icon="arrow"
                  href={active.href}
                  external
                  onClick={() => setActive(null)}
                >
                  {active.ctaLabel || "Open"}
                </Button>
              ) : (
                <Button icon="arrow" size="md" onClick={() => navigate("/contact")}>
                  Get in touch
                </Button>
              )}
              <Button variant="outline" size="md" onClick={() => setActive(null)}>
                Close
              </Button>
            </div>

            {active.href && (
              <p className="mt-5 flex items-center gap-1.5 text-[13px] text-[#7A6F61]">
                <Icon name="external" className="h-3.5 w-3.5 shrink-0" />
                Opens {new URL(active.href).hostname} in a new tab
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ServicesPage;
